import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Car,
  ShoppingCart,
  Settings,
  Users,
  Search,
  CalendarCheck,
  MapPin,
  Shield,
  Clock,
  Star,
  ChevronRight,
  Menu,
  X,
  Phone,
  Mail,
  ArrowRight,
  Truck,
  Bike,
  Bus,
  Globe,
  Gift,
  Zap,
  Lock,
} from 'lucide-react';
import appConfig from '../../config/appConfig';
import { useAuth } from '../../context/AuthContext';
import SEO from '../../components/SEO';

const FEATURES = [
  {
    icon: Shield,
    title: 'Verified Owners',
    description: 'Every vehicle owner is verified with documents before listing. No surprises, no scams.',
    color: 'bg-blue-500',
  },
  {
    icon: Users,
    title: 'Per-Seat Sharing',
    description: 'Pay only for your seat on intercity one-way trips. Split costs, ride safe with verified drivers.',
    color: 'bg-emerald-500',
  },
  {
    icon: CalendarCheck,
    title: 'Tour Packages from Agencies',
    description: 'Tirupati darshan, Coorg weekends, custom routes — curated by verified travel agencies.',
    color: 'bg-indigo-500',
  },
  {
    icon: Gift,
    title: 'Free for Owners',
    description: 'No listing fees. No platform subscription. Pay only when a booking comes through.',
    color: 'bg-amber-500',
  },
  {
    icon: Zap,
    title: 'Instant Booking',
    description: 'Skip the back-and-forth. Confirm your seat, car, or package in under a minute.',
    color: 'bg-rose-500',
  },
  {
    icon: Lock,
    title: 'Privacy by Default',
    description: 'Driver and customer phone numbers stay hidden until a booking is confirmed.',
    color: 'bg-slate-700',
  },
];

const HOW_IT_WORKS = [
  {
    step: '01',
    icon: Search,
    title: 'Search & Browse',
    description: 'Find the perfect vehicle from our wide selection of cars, bikes, buses and trucks.',
  },
  {
    step: '02',
    icon: CalendarCheck,
    title: 'Book Instantly',
    description: 'Choose your dates, pick up location, and book your vehicle in seconds.',
  },
  {
    step: '03',
    icon: MapPin,
    title: 'Drive & Enjoy',
    description: 'Pick up your vehicle and hit the road. Track your trip in real-time.',
  },
];

const VEHICLE_TYPES = [
  { icon: Car, label: 'Cars', color: 'from-blue-500 to-blue-600', iconColor: 'text-white' },
  { icon: Bike, label: 'Bikes', color: 'from-green-500 to-green-600', iconColor: 'text-white' },
  { icon: Bus, label: 'Buses', color: 'from-orange-500 to-orange-600', iconColor: 'text-white' },
  { icon: Truck, label: 'Trucks', color: 'from-purple-500 to-purple-600', iconColor: 'text-white' },
];


const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:7000/api';

const HomeEnquiryForm = () => {
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  if (success) {
    return (
      <div className="relative bg-white/80 backdrop-blur-md rounded-2xl shadow-xl shadow-blue-900/5 border border-white/60 p-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-lg shadow-emerald-500/30 mb-4">
          <Shield className="h-7 w-7 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-1.5">Enquiry Sent!</h3>
        <p className="text-sm text-gray-600 mb-5">We'll contact you within 24 hours.</p>
        <button onClick={() => { setSuccess(false); setName(''); setContact(''); setMessage(''); }} className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline">Send another</button>
      </div>
    );
  }

  return (
    <form onSubmit={async (e) => {
      e.preventDefault();
      if (!name.trim() || !contact.trim() || !message.trim()) { setError('All fields are required'); return; }
      setLoading(true); setError('');
      try {
        await fetch(API_BASE + '/users/enquiry', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, contact, message }) });
        setSuccess(true);
      } catch { setError('Failed to send. Please try again.'); }
      finally { setLoading(false); }
    }} className="relative bg-white/80 backdrop-blur-md rounded-2xl shadow-xl shadow-blue-900/5 border border-white/60 p-6 sm:p-8 space-y-4">
      <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your Name *"
        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all" required />
      <input type="text" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Phone or Email *"
        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all" required />
      <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Write your enquiry... *" rows={3}
        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all resize-none" required />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button type="submit" disabled={loading}
        className="group w-full py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all shadow-lg shadow-blue-600/30 hover:shadow-xl hover:shadow-blue-600/40 hover:-translate-y-0.5 flex items-center justify-center gap-2">
        {loading ? 'Sending...' : (<>
          Send Enquiry
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </>)}
      </button>
    </form>
  );
};

export default function Home() {
  const { user: authUser } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Service tabs (What are you looking for?) — top-level + Rental sub-tabs
  const [serviceTab, setServiceTab] = useState('rental');
  const [rentalSub, setRentalSub] = useState('daily');
  const [browseData, setBrowseData] = useState({});
  const [tabOneway, setTabOneway] = useState([]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch a small sample of data for each service tab on mount
  useEffect(() => {
    const api = process.env.REACT_APP_API_URL || 'http://localhost:7000/api';
    fetch(`${api}/cars/browse`).then(r => r.json()).then(d => {
      if (d && d.success) setBrowseData(d.categories || {});
    }).catch(() => {});
    fetch(`${api}/oneway-trips`).then(r => r.json()).then(d => {
      const trips = d?.trips || d?.data?.trips || d?.data || [];
      setTabOneway(Array.isArray(trips) ? trips.slice(0, 4) : []);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-gray-900">
      <SEO title={appConfig.tagline} description={appConfig.description} path="/" noTitleSuffix={false} />
      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-gray-900/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link to={authUser ? '/dashboard' : '/home'} className="flex items-center gap-2 hover:opacity-90 transition-opacity">
              <img src={appConfig.logo} alt={appConfig.name} className="w-10 h-10 object-contain rounded-lg" />
              <span className="text-xl sm:text-2xl font-bold text-white">{appConfig.name}</span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/browse" className="text-gray-300 hover:text-white transition-colors text-sm font-medium">
                Browse Vehicles
              </Link>
              <Link to="/pricing" className="text-gray-300 hover:text-white transition-colors text-sm font-medium">
                Pricing
              </Link>
              <Link to="/about" className="text-gray-300 hover:text-white transition-colors text-sm font-medium">
                About Us
              </Link>
              <Link to="/support" className="text-gray-300 hover:text-white transition-colors text-sm font-medium">
                Support
              </Link>
              <Link to="/track-booking" className="text-gray-300 hover:text-white transition-colors text-sm font-medium">
                Track Booking
              </Link>
            </div>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-3">
              <Link
                to="/login"
                className="px-4 py-2 text-sm font-medium text-white border border-white/30 rounded-lg hover:bg-white/10 transition-colors"
              >
                Login
              </Link>
              <Link
                to="/register"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Register
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-gray-900/95 backdrop-blur-md border-t border-white/10">
            <div className="px-4 py-4 space-y-3">
              <Link to="/browse" className="block text-gray-300 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                Browse Vehicles
              </Link>
              <Link to="/pricing" className="block text-gray-300 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                Pricing
              </Link>
              <Link to="/about" className="block text-gray-300 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                About Us
              </Link>
              <Link to="/support" className="block text-gray-300 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                Support
              </Link>
              <Link to="/track-booking" className="block text-gray-300 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                Track Booking
              </Link>
              <div className="flex gap-3 pt-3 border-t border-white/10">
                <Link
                  to="/login"
                  className="flex-1 text-center px-4 py-2 text-sm font-medium text-white border border-white/30 rounded-lg"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="flex-1 text-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Register
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section with Video Background */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16 sm:pt-20 pb-8">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          poster="https://images.pexels.com/videos/2103099/free-video-2103099.jpg?auto=compress&cs=tinysrgb&w=1920"
        >
          <source
            src="https://videos.pexels.com/video-files/2103099/2103099-uhd_2560_1440_30fps.mp4"
            type="video/mp4"
          />
        </video>

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/70 via-gray-900/60 to-gray-900" />

        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          {/* Vehicle Type Icons — monochrome glass tiles, hidden on mobile */}
          <div className="hidden sm:flex justify-center gap-6 mb-8">
            {VEHICLE_TYPES.map(({ icon: Icon, label }) => (
              <div key={label} className="flex flex-col items-center gap-2 group">
                <div className="p-3 rounded-xl bg-white/5 backdrop-blur-md border border-white/15 group-hover:bg-white/10 group-hover:border-white/30 transition-all duration-300">
                  <Icon className="w-7 h-7 text-white/90" />
                </div>
                <span className="text-xs font-medium text-white/70">{label}</span>
              </div>
            ))}
          </div>

          {/* Title — cinematic gradient + soft backlight + fade-up */}
          <div className="relative mb-3 sm:mb-4 opacity-0 animate-fade-up" style={{ animationDelay: '120ms' }}>
            {/* Soft backlight glow behind the title */}
            <div aria-hidden className="absolute inset-0 blur-3xl opacity-50 pointer-events-none">
              <div className="h-full w-full bg-gradient-to-r from-blue-500/40 via-indigo-400/30 to-blue-500/40 rounded-full"></div>
            </div>
            <h1
              className="relative text-4xl sm:text-6xl md:text-7xl font-extrabold tracking-tight text-white"
              style={{
                filter: 'drop-shadow(0 0 14px rgba(255,255,255,0.35)) drop-shadow(0 6px 20px rgba(0,0,0,0.8))',
              }}
            >
              {appConfig.name.split('4').map((part, i, arr) => (
                <React.Fragment key={i}>
                  {part}
                  {i < arr.length - 1 && (
                    <span
                      className="text-yellow-300 drop-shadow-[0_2px_10px_rgba(253,224,71,0.55)]"
                    >
                      4
                    </span>
                  )}
                </React.Fragment>
              ))}
              <span className="text-white text-2xl sm:text-3xl md:text-4xl font-bold align-baseline ml-0.5">
                .in
              </span>
            </h1>
          </div>

          {/* Tagline — simple white, styled separators + fade-up */}
          <div className="opacity-0 animate-fade-up mb-2 sm:mb-3" style={{ animationDelay: '320ms' }}>
            <p className="text-base sm:text-xl md:text-2xl font-semibold tracking-wide">
              {appConfig.tagline.split('|').map((word, i, arr) => (
                <span key={i}>
                  <span className="text-sky-300 drop-shadow-[0_2px_8px_rgba(125,211,252,0.3)]">{word.trim()}</span>
                  {i < arr.length - 1 && <span className="mx-2 sm:mx-3 text-sky-300/30 font-light">|</span>}
                </span>
              ))}
            </p>
          </div>

          {/* Description — slower fade-up */}
          <p className="opacity-0 animate-fade-up text-sm sm:text-lg text-gray-300 mb-6 sm:mb-10 max-w-2xl mx-auto" style={{ animationDelay: '520ms' }}>
            {appConfig.description}. Book your ride in seconds and hit the road with confidence.
          </p>

          {/* Primary CTA — single big Browse button + Owner Login link */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Link
              to="/browse?tab=all"
              className="group inline-flex items-center justify-center gap-2 px-7 sm:px-8 py-3 sm:py-3.5 text-base sm:text-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-xl shadow-blue-900/50 hover:shadow-2xl hover:shadow-blue-700/60 hover:-translate-y-0.5"
            >
              <Search className="w-5 h-5" />
              Browse Vehicles
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 sm:py-3.5 text-sm sm:text-base font-medium text-white border border-white/30 rounded-xl hover:bg-white/10 transition-all"
            >
              <Users className="w-4 h-4" />
              Vehicle Owner? Login
            </Link>
          </div>

          {/* Trust Indicators — monochrome white for cohesion */}
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-white/70">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-white/80" />
              <span>Verified Owners</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-white/80" />
              <span>Instant Booking</span>
            </div>
            <div className="flex items-center gap-2">
              <Gift className="w-4 h-4 text-white/80" />
              <span>Free to Join</span>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronRight className="w-6 h-6 text-white/50 rotate-90" />
        </div>
      </section>

      {/* What are you looking for? — Service tabs: Rental / One-Way / Packages / Buy or Sell */}
      <section className="py-8 sm:py-10 bg-gradient-to-b from-blue-50 via-sky-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4 sm:mb-5">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">What are you looking for?</h2>
            <p className="text-sm text-gray-500 mt-0.5">Pick a service to get started</p>
          </div>

          {/* Service tab row — segmented control. Horizontally scrolls on
              mobile so labels stay on one line and never wrap. */}
          <div className="flex justify-start mb-4 sm:mb-5 -mx-4 sm:mx-0 overflow-x-auto scrollbar-hide">
            <div className="inline-flex items-center bg-gray-100 p-1.5 rounded-full shadow-inner mx-4 sm:mx-0">
              {[
                { id: 'rental', label: 'Rental', icon: Car },
                { id: 'oneway', label: 'One-Way', icon: MapPin },
                { id: 'sale', label: 'Buy', icon: ShoppingCart },
                { id: 'packages', label: 'Packages', icon: CalendarCheck }
              ].map(t => {
                const active = serviceTab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setServiceTab(t.id)}
                    className={`relative flex items-center gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap flex-shrink-0 transition-all duration-200 ease-out ${
                      active ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <t.icon className="h-4 w-4 flex-shrink-0" />
                    <span>{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content panel — soft blue gradient, gentle crossfade */}
          <div key={serviceTab} className="animate-fade-in bg-gradient-to-b from-blue-200 via-sky-100 to-white rounded-2xl border border-blue-100 shadow-sm p-5 sm:p-8 min-h-[420px]">
            {/* RENTAL */}
            {serviceTab === 'rental' && (
              <>
                {/* Rental sub-tabs */}
                <div className="flex flex-wrap gap-2 mb-5 border-b border-gray-100 pb-4">
                  {[
                    { id: 'daily', label: 'Daily Rental' },
                    { id: 'rental', label: 'Multi-Day Rental' },
                    { id: 'kmBased', label: 'Per-KM Rental' }
                  ].map(s => {
                    const active = rentalSub === s.id;
                    return (
                      <button key={s.id} onClick={() => setRentalSub(s.id)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          active ? 'bg-gray-900 text-white ring-1 ring-gray-900' : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}>
                        {s.label}
                      </button>
                    );
                  })}
                </div>

                {/* Rental cars grid */}
                {(() => {
                  const cars = (browseData[rentalSub]?.cars || []).slice(0, 4);
                  if (cars.length === 0) {
                    return (
                      <div className="text-center py-12 px-4">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-50 mb-4">
                          <Car className="h-8 w-8 text-blue-500" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900 mb-1.5">No vehicles available yet</h3>
                        <p className="text-sm text-gray-500 max-w-sm mx-auto mb-5">We're onboarding owners across Karnataka. Check back soon or browse all rentals.</p>
                        <Link to={`/browse?service=rental&tab=${rentalSub === 'kmBased' ? 'km-based' : rentalSub}`}
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition-all hover:shadow-md hover:-translate-y-0.5">
                          Browse All Rentals
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </div>
                    );
                  }
                  return (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {cars.map(c => (
                          <Link key={c._id} to={`/browse?service=rental&tab=${rentalSub === 'kmBased' ? 'km-based' : rentalSub}`}
                            className="group bg-white rounded-xl border border-gray-100 hover:shadow-md hover:-translate-y-0.5 transition-all overflow-hidden">
                            <div className="h-32 bg-gray-100 overflow-hidden">
                              {c.images?.[0] ? <img src={c.images[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : <div className="w-full h-full flex items-center justify-center"><Car className="h-10 w-10 text-gray-300" /></div>}
                            </div>
                            <div className="p-3">
                              <p className="text-sm font-bold text-gray-900 truncate">{c.make} {c.model}</p>
                              <p className="text-xs text-gray-500">{c.year} · {c.fuelType || ''}</p>
                              {(c.pricePerDay || c.pricePerKm) && (
                                <p className="mt-1 text-sm font-semibold text-blue-600">
                                  ₹{(rentalSub === 'kmBased' ? c.pricePerKm : c.pricePerDay)?.toLocaleString('en-IN')}<span className="text-[10px] text-gray-400 ml-0.5">/{rentalSub === 'kmBased' ? 'km' : 'day'}</span>
                                </p>
                              )}
                            </div>
                          </Link>
                        ))}
                      </div>
                      <div className="text-center mt-6">
                        <Link to={`/browse?service=rental&tab=${rentalSub === 'kmBased' ? 'km-based' : rentalSub}`}
                          className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700">
                          Browse all rentals →
                        </Link>
                      </div>
                    </>
                  );
                })()}
              </>
            )}

            {/* ONE-WAY */}
            {serviceTab === 'oneway' && (
              <>
                {tabOneway.length === 0 ? (
                  <div className="text-center py-12 px-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-50 mb-4">
                      <MapPin className="h-8 w-8 text-blue-500" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-1.5">No one-way trips scheduled</h3>
                    <p className="text-sm text-gray-500 max-w-sm mx-auto mb-5">Drivers post intercity trips here so you can grab a seat at a lower price. Search to see what's coming up.</p>
                    <Link to="/browse?service=oneway&tab=oneway"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition-all hover:shadow-md hover:-translate-y-0.5">
                      Search Available Trips
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {tabOneway.map(t => (
                        <Link key={t._id} to="/browse?service=oneway&tab=oneway"
                          className="group bg-white rounded-xl border border-gray-100 hover:shadow-md hover:-translate-y-0.5 transition-all p-4">
                          <div className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-2">
                            <span>{t.fromCity}</span>
                            <span className="text-blue-500">→</span>
                            <span>{t.toCity}</span>
                          </div>
                          <p className="text-xs text-gray-500 mb-2">{new Date(t.departureDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}{t.departureTime ? ` · ${t.departureTime}` : ''}</p>
                          <p className="text-sm font-semibold text-blue-600">₹{(t.price || 0).toLocaleString('en-IN')}<span className="text-[10px] text-gray-400 ml-0.5">/seat</span></p>
                          <p className="text-[11px] text-gray-400 mt-1">{t.seatsAvailable} seat{t.seatsAvailable !== 1 ? 's' : ''} left</p>
                        </Link>
                      ))}
                    </div>
                    <div className="text-center mt-6">
                      <Link to="/browse?service=oneway&tab=oneway" className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700">Find more trips →</Link>
                    </div>
                  </>
                )}
              </>
            )}

            {/* PACKAGES */}
            {serviceTab === 'packages' && (
              <div className="text-center py-12 px-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-50 mb-4">
                  <CalendarCheck className="h-8 w-8 text-blue-500" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1.5">Tour Packages from Travel Agencies</h3>
                <p className="text-sm text-gray-500 max-w-md mx-auto mb-5">
                  Curated trip packages — Tirupati darshan, weekend getaways, outstation trips and more — directly from verified travel agencies.
                </p>
                <Link to="/browse?service=packages&tab=all"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition-all hover:shadow-md hover:-translate-y-0.5">
                  Explore Travel Agencies
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            )}

            {/* BUY / SELL */}
            {serviceTab === 'sale' && (
              <>
                {(() => {
                  const cars = (browseData.sale?.cars || []).slice(0, 4);
                  if (cars.length === 0) {
                    return (
                      <div className="text-center py-12 px-4">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-50 mb-4">
                          <ShoppingCart className="h-8 w-8 text-blue-500" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900 mb-1.5">No vehicles listed for sale yet</h3>
                        <p className="text-sm text-gray-500 max-w-sm mx-auto mb-5">Owners list their pre-owned cars, bikes, and commercial vehicles here. Verified listings only.</p>
                        <Link to="/browse?service=sale&tab=sale"
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition-all hover:shadow-md hover:-translate-y-0.5">
                          Browse All Listings
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </div>
                    );
                  }
                  return (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {cars.map(c => (
                          <Link key={c._id} to="/browse?service=sale&tab=sale"
                            className="group bg-white rounded-xl border border-gray-100 hover:shadow-md hover:-translate-y-0.5 transition-all overflow-hidden">
                            <div className="h-32 bg-gray-100 overflow-hidden">
                              {c.images?.[0] ? <img src={c.images[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : <div className="w-full h-full flex items-center justify-center"><Car className="h-10 w-10 text-gray-300" /></div>}
                            </div>
                            <div className="p-3">
                              <p className="text-sm font-bold text-gray-900 truncate">{c.make} {c.model}</p>
                              <p className="text-xs text-gray-500">{c.year} · {c.fuelType || ''}</p>
                              {c.salePrice && <p className="mt-1 text-sm font-semibold text-blue-600">₹{c.salePrice.toLocaleString('en-IN')}</p>}
                            </div>
                          </Link>
                        ))}
                      </div>
                      <div className="text-center mt-6">
                        <Link to="/browse?service=sale&tab=sale" className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700">Browse all listings →</Link>
                      </div>
                    </>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Vehicle Categories — dark glass treatment */}
      <section className="relative py-12 sm:py-16 overflow-hidden bg-gradient-to-b from-slate-900 via-blue-950 to-slate-900">
        {/* Decorative glow blobs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl -translate-y-1/2 pointer-events-none"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl translate-y-1/2 pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Browse by Vehicle Type</h2>
            <p className="text-sm sm:text-base text-blue-200">Find the perfect vehicle for your needs</p>
          </div>
          {(() => {
            const vehicles = [
              { name: 'Car', img: 'https://images.pexels.com/photos/170811/pexels-photo-170811.jpeg?auto=compress&cs=tinysrgb&w=300', type: 'car' },
              { name: 'SUV', img: 'https://images.pexels.com/photos/116675/pexels-photo-116675.jpeg?auto=compress&cs=tinysrgb&w=300', type: 'suv' },
              { name: 'Bike', img: 'https://images.pexels.com/photos/10138605/pexels-photo-10138605.jpeg?auto=compress&cs=tinysrgb&w=300', type: 'bike' },
              { name: 'Scooter', img: 'https://images.pexels.com/photos/1192330/pexels-photo-1192330.jpeg?auto=compress&cs=tinysrgb&w=300', type: 'scooter' },
              { name: 'Auto', img: 'https://images.pexels.com/photos/30778957/pexels-photo-30778957.jpeg?auto=compress&cs=tinysrgb&w=300', type: 'auto' },
              { name: 'Bus', img: 'https://images.pexels.com/photos/3608967/pexels-photo-3608967.jpeg?auto=compress&cs=tinysrgb&w=300', type: 'bus' },
              { name: 'Tempo', img: 'https://images.pexels.com/photos/1007410/pexels-photo-1007410.jpeg?auto=compress&cs=tinysrgb&w=300', type: 'tempo-traveller' },
              { name: 'Truck', img: 'https://images.pexels.com/photos/29057947/pexels-photo-29057947.jpeg?auto=compress&cs=tinysrgb&w=300', type: 'truck' },
              { name: 'Van', img: 'https://images.pexels.com/photos/9115461/pexels-photo-9115461.jpeg?auto=compress&cs=tinysrgb&w=300', type: 'van' },
              { name: 'Pickup', img: 'https://images.pexels.com/photos/2994335/pexels-photo-2994335.jpeg?auto=compress&cs=tinysrgb&w=300', type: 'pickup' },
              { name: 'Tractor', img: 'https://images.pexels.com/photos/3793/agriculture-tractor.jpg?auto=compress&cs=tinysrgb&w=300', type: 'tractor' },
              { name: 'JCB', img: 'https://images.pexels.com/photos/1078884/pexels-photo-1078884.jpeg?auto=compress&cs=tinysrgb&w=300', type: 'jcb' },
              { name: 'Ambulance', img: 'https://images.pexels.com/photos/6519838/pexels-photo-6519838.jpeg?auto=compress&cs=tinysrgb&w=300', type: 'ambulance' },
            ];
            // Duplicate for infinite scroll effect
            const items = [...vehicles, ...vehicles];
            return (
              <div className="overflow-hidden">
                <div className="flex gap-5 animate-marquee hover:pause-marquee">
                  {items.map((v, i) => (
                    <Link key={v.name + i} to={`/browse?type=${v.type}`} className="flex-shrink-0 group">
                      <div className="w-36 sm:w-44 bg-white/5 backdrop-blur-md rounded-xl overflow-hidden border border-white/10 hover:bg-white/10 hover:border-white/30 hover:-translate-y-1 hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-300">
                        <div className="h-24 sm:h-28 overflow-hidden">
                          <img src={v.img} alt={v.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                        </div>
                        <p className="text-center py-2.5 text-sm font-semibold text-white">{v.name}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* Features Section — light premium with glow blobs + glass cards */}
      <section className="relative py-12 sm:py-16 overflow-hidden bg-gradient-to-b from-white via-blue-50/40 to-white">
        {/* Decorative glow blobs */}
        <div className="absolute top-0 right-0 w-[28rem] h-[28rem] bg-blue-300/20 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[28rem] h-[28rem] bg-indigo-300/20 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3 pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              Why {appConfig.name}
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Trusted by riders and owners alike. Here's what sets us apart.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {FEATURES.map(({ icon: Icon, title, description, color }) => (
              <div
                key={title}
                className="group relative bg-white/80 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border border-gray-100 hover:bg-white hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className={`${color} w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-md`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1.5">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works — cinematic road journey */}
      <section className="relative py-16 sm:py-24 overflow-hidden bg-slate-950">
        {/* Real road photo background */}
        <div className="absolute inset-0">
          <img
            src="https://res.cloudinary.com/dfg39dktd/image/upload/v1779262734/car-booking/branding/browse-background.jpg"
            alt=""
            className="w-full h-full object-cover opacity-40"
          />
          {/* Cinematic gradient overlay (dark blue → black) */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/85 via-slate-900/70 to-slate-950/95"></div>
          {/* Vignette */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(2,6,23,0.6)_85%)]"></div>
        </div>

        {/* Atmospheric glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60rem] h-64 bg-blue-500/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[60rem] h-64 bg-amber-500/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-yellow-300 mb-3">The Journey</p>
            <h2 className="text-3xl sm:text-5xl font-bold text-white mb-4">How It Works</h2>
            <p className="text-base sm:text-lg text-blue-100/80 max-w-2xl mx-auto">
              Get on the road in 3 simple steps
            </p>
          </div>

          {/* Road map — cinematic glowing pins on a road */}
          {(() => {
            const accents = [
              { badge: 'bg-gradient-to-br from-blue-400 to-blue-600',     glow: 'shadow-[0_0_40px_rgba(59,130,246,0.6)]',   ring: 'ring-blue-400/30' },
              { badge: 'bg-gradient-to-br from-amber-400 to-orange-500',  glow: 'shadow-[0_0_40px_rgba(245,158,11,0.6)]',    ring: 'ring-amber-400/30' },
              { badge: 'bg-gradient-to-br from-emerald-400 to-emerald-600', glow: 'shadow-[0_0_40px_rgba(16,185,129,0.6)]', ring: 'ring-emerald-400/30' },
            ];
            return (
              <div className="max-w-5xl mx-auto">
                {/* DESKTOP — horizontal cinematic road */}
                <div className="hidden md:block relative pt-2 pb-2">
                  {/* Asphalt road bar with edge highlights + glowing dashed center */}
                  <div className="absolute top-10 left-8 right-8 h-5 -translate-y-px rounded-full bg-gradient-to-b from-slate-700 via-slate-900 to-black shadow-[inset_0_2px_8px_rgba(0,0,0,0.6),0_2px_20px_rgba(0,0,0,0.5)] z-0">
                    {/* Top edge highlight (catches the light) */}
                    <div className="absolute top-0 left-2 right-2 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
                    {/* Glowing dashed yellow center line */}
                    <div
                      className="absolute top-1/2 left-3 right-3 h-[2px] -translate-y-1/2"
                      style={{
                        backgroundImage: 'repeating-linear-gradient(to right, rgba(250,204,21,0.95) 0 18px, transparent 18px 30px)',
                        boxShadow: '0 0 12px rgba(250,204,21,0.5)',
                      }}
                    ></div>
                  </div>

                  {/* Start (yellow pulse) + Finish (emerald) markers */}
                  <div className="absolute top-10 left-8 -translate-y-1/2 -translate-x-1/2 z-10">
                    <div className="w-4 h-4 bg-yellow-300 rounded-full ring-4 ring-yellow-300/40 shadow-[0_0_24px_rgba(250,204,21,0.8)] animate-pulse"></div>
                  </div>
                  <div className="absolute top-10 right-8 -translate-y-1/2 translate-x-1/2 z-10">
                    <div className="w-4 h-4 bg-emerald-400 rounded-full ring-4 ring-emerald-400/40 shadow-[0_0_24px_rgba(52,211,153,0.8)]"></div>
                  </div>

                  {/* Pins + content grid */}
                  <div className="relative grid grid-cols-3 mx-8 gap-6">
                    {HOW_IT_WORKS.map(({ step, icon: Icon, title, description }, index) => {
                      const a = accents[index] || accents[0];
                      return (
                        <div key={step} className="flex flex-col items-center group">
                          {/* Headlight-style glowing pin on the road */}
                          <div className="relative z-10">
                            <div className={`w-20 h-20 rounded-full ${a.badge} flex items-center justify-center ${a.glow} ring-4 ${a.ring} group-hover:scale-110 group-hover:-translate-y-1 transition-all duration-500`}>
                              <Icon className="w-8 h-8 text-white drop-shadow-md" />
                            </div>
                            {/* Glow ring under the pin (light cast on road) */}
                            <div className={`absolute -bottom-2 left-1/2 -translate-x-1/2 w-16 h-2 ${a.badge} rounded-full blur-md opacity-70`}></div>
                            <span className="absolute -top-2 -right-2 bg-white text-gray-900 text-xs font-black w-7 h-7 rounded-full flex items-center justify-center ring-2 ring-slate-900/20 shadow-lg">{step}</span>
                          </div>
                          {/* Glass card below */}
                          <div className="mt-8 text-center max-w-xs bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl px-5 py-4 group-hover:bg-white/10 group-hover:border-white/20 transition-all duration-300">
                            <h3 className="text-lg font-semibold text-white mb-1.5">{title}</h3>
                            <p className="text-sm text-blue-100/70 leading-relaxed">{description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* MOBILE — vertical cinematic road */}
                <div className="md:hidden">
                  {HOW_IT_WORKS.map(({ step, icon: Icon, title, description }, index) => {
                    const a = accents[index] || accents[0];
                    const isLast = index === HOW_IT_WORKS.length - 1;
                    return (
                      <div key={step} className="relative flex gap-4">
                        {/* Glowing pin + vertical road segment */}
                        <div className="relative flex flex-col items-center">
                          <div className={`w-16 h-16 rounded-full ${a.badge} flex items-center justify-center ${a.glow} ring-4 ${a.ring} relative z-10 flex-shrink-0`}>
                            <Icon className="w-7 h-7 text-white drop-shadow" />
                            <span className="absolute -top-1.5 -right-1.5 bg-white text-gray-900 text-[10px] font-black w-6 h-6 rounded-full flex items-center justify-center ring-2 ring-slate-900/20 shadow">{step}</span>
                          </div>
                          {!isLast && (
                            <div className="flex-1 w-4 mt-2 mb-1 rounded-full bg-gradient-to-b from-slate-700 via-slate-900 to-slate-700 shadow-[inset_0_2px_6px_rgba(0,0,0,0.6)] relative">
                              <div
                                className="absolute left-1/2 top-2 bottom-2 -translate-x-1/2 w-[2px]"
                                style={{
                                  backgroundImage: 'repeating-linear-gradient(to bottom, rgba(250,204,21,0.95) 0 12px, transparent 12px 22px)',
                                  boxShadow: '0 0 10px rgba(250,204,21,0.5)',
                                }}
                              ></div>
                            </div>
                          )}
                        </div>
                        {/* Glass content card */}
                        <div className="pb-8 flex-1">
                          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl px-4 py-3.5">
                            <h3 className="text-base font-semibold text-white mb-1">{title}</h3>
                            <p className="text-sm text-blue-100/70 leading-relaxed">{description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          <div className="text-center mt-10">
            <Link
              to="/browse"
              className="group inline-flex items-center gap-2 px-7 py-3.5 text-base sm:text-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg shadow-blue-600/30 hover:shadow-xl hover:shadow-blue-600/40 hover:-translate-y-0.5"
            >
              Get Started Now
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section — modern split: For Customers / For Owners */}
      <section className="relative py-14 sm:py-20 overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
        {/* Decorative glow blobs */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-3">Ready to Roll?</h2>
            <p className="text-base sm:text-lg text-blue-200 max-w-2xl mx-auto">
              Whether you need a ride or own one to share — get started in under a minute.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            {/* For Customers */}
            <div className="group relative bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 sm:p-8 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 ring-1 ring-blue-400/30 flex items-center justify-center mb-4">
                <Car className="h-6 w-6 text-blue-300" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2">I want to ride</h3>
              <p className="text-sm sm:text-base text-blue-100/80 mb-6">
                Browse vehicles, packages, and one-way trips across Karnataka. Instant booking, verified owners.
              </p>
              <Link to="/browse?tab=all"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-blue-700 rounded-xl text-sm font-semibold hover:bg-blue-50 transition-all group-hover:shadow-lg group-hover:shadow-blue-500/30">
                Browse Vehicles
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* For Owners */}
            <div className="group relative bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 sm:p-8 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 ring-1 ring-amber-400/30 flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-amber-300" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2">I own a vehicle</h3>
              <p className="text-sm sm:text-base text-blue-100/80 mb-6">
                List your car, bike, or commercial vehicle. Get bookings without needing your own website. Free to register.
              </p>
              <Link to="/register"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-blue-700 rounded-xl text-sm font-semibold hover:bg-blue-50 transition-all group-hover:shadow-lg group-hover:shadow-amber-500/30">
                Register as Owner
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Enquiry Section — light premium with glow blobs + glass form */}
      <section className="relative py-12 sm:py-16 overflow-hidden bg-gradient-to-b from-white via-indigo-50/40 to-white">
        {/* Decorative glow blobs */}
        <div className="absolute top-0 left-1/4 w-[28rem] h-[28rem] bg-blue-300/20 rounded-full blur-3xl -translate-y-1/2 pointer-events-none"></div>
        <div className="absolute bottom-0 right-1/4 w-[28rem] h-[28rem] bg-indigo-300/20 rounded-full blur-3xl translate-y-1/2 pointer-events-none"></div>

        <div className="relative max-w-xl mx-auto px-4">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/30 mb-4">
              <Mail className="h-7 w-7 text-white" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Have a Question?</h2>
            <p className="text-base text-gray-600">Send us your enquiry and we'll get back within 24 hours.</p>
          </div>
          <HomeEnquiryForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="md:col-span-1">
              <Link to={authUser ? '/dashboard' : '/home'} className="flex items-center gap-2 mb-4 hover:opacity-90 transition-opacity">
                <img src={appConfig.logo} alt={appConfig.name} className="w-10 h-10 object-contain rounded-lg" />
                <span className="text-lg font-bold text-white">{appConfig.name}</span>
              </Link>
              <p className="text-sm text-gray-500 mb-4">{appConfig.description}</p>
              <div className="flex flex-col gap-2 text-sm">
                <a href={`tel:${appConfig.contact.phone}`} className="flex items-center gap-2 hover:text-white transition-colors">
                  <Phone className="w-4 h-4" /> {appConfig.contact.phone}
                </a>
                <a href={`mailto:${appConfig.contact.email}`} className="flex items-center gap-2 hover:text-white transition-colors">
                  <Mail className="w-4 h-4" /> {appConfig.contact.email}
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2 text-sm">
                <Link to="/browse" className="block hover:text-white transition-colors">Browse Vehicles</Link>
                <Link to="/track-booking" className="block hover:text-white transition-colors">Track Booking</Link>
                <Link to="/login" className="block hover:text-white transition-colors">Owner Login</Link>
                <Link to="/register" className="block hover:text-white transition-colors">Register</Link>
              </div>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <div className="space-y-2 text-sm">
                <Link to="/about" className="block hover:text-white transition-colors">About Us</Link>
                <Link to="/support" className="block hover:text-white transition-colors">Support</Link>
                <Link to="/terms" className="block hover:text-white transition-colors">Terms & Conditions</Link>
                <Link to="/privacy" className="block hover:text-white transition-colors">Privacy Policy</Link>
                <Link to="/refund-policy" className="block hover:text-white transition-colors">Refund Policy</Link>
                <Link to="/grievance" className="block hover:text-white transition-colors">Grievance Officer</Link>
              </div>
            </div>

            {/* For Owners */}
            <div>
              <h4 className="text-white font-semibold mb-4">For Vehicle Owners</h4>
              <ul className="space-y-2 text-sm">
                {appConfig.ownerBenefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-10 pt-6 text-center text-sm text-gray-500">
            {appConfig.copyright}
          </div>
        </div>
      </footer>
    </div>
  );
}
